<script setup lang="ts">
import type { UserAttendeeView } from '#shared/types/user-attendees'
import { calculateAge, getAttendeeDisplayName, maskRijksregisternummer } from '#shared/types/user-attendees'
import { useUserAttendees } from '~/composables/useUserAttendees'
import { useUserAttendeeSaves } from '~/composables/useUserAttendeeSaves'
import { formatDate } from '~/utils/dates'
import { logApiError } from '~/utils/logger'

interface Props {
  modelValue?: UserAttendeeView | null
  disabled?: boolean
  disabledIds?: string[]
  hasSelection?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  disabled: false,
  disabledIds: () => [],
  hasSelection: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: UserAttendeeView | null]
  'select': [attendee: UserAttendeeView]
  'new': []
}>()

const { getAttendees } = useUserAttendees()
const { flushAttendeeSaves } = useUserAttendeeSaves()
const authStore = useAuthStore()

// State
const isOpen = ref(false)
const isLoading = ref(false)
const isInitialLoadDone = ref(false)
const attendees = ref<UserAttendeeView[]>([])
const searchQuery = ref('')

// Check if user has any saved attendees
const hasSavedAttendees = computed(() => isInitialLoadDone.value && attendees.value.length > 0)

// Load attendees when dialog opens
const loadAttendees = async () => {
  if (!authStore.user)
    return

  isLoading.value = true
  try {
    const result = await getAttendees()
    if (result.success && result.data)
      attendees.value = result.data
  }
  catch (err) {
    logApiError('/user-attendees/load', err)
  }
  finally {
    isLoading.value = false
    isInitialLoadDone.value = true
  }
}

// Filtered attendees based on search
const filteredAttendees = computed(() => {
  if (!searchQuery.value.trim())
    return attendees.value

  const query = searchQuery.value.toLowerCase()

  return attendees.value.filter(attendee => {
    const fullName = `${attendee.firstName} ${attendee.lastName}`.toLowerCase()
    const nickname = attendee.nickname?.toLowerCase() || ''

    return fullName.includes(query) || nickname.includes(query)
  })
})

const isAttendeeDisabled = (attendee: UserAttendeeView) => {
  return props.disabledIds?.includes(attendee.id)
}

// Select an attendee
const selectAttendee = (attendee: UserAttendeeView) => {
  if (isAttendeeDisabled(attendee))
    return

  emit('update:modelValue', attendee)
  emit('select', attendee)
  isOpen.value = false
}

// Clear selection
const clearSelection = () => {
  emit('update:modelValue', null)
}

// Open dialog and load attendees
const openPicker = async () => {
  if (props.disabled)
    return

  // Wait for any in-flight auto-saves from sibling ticket cards so newly
  // saved attendees show up in the list immediately.
  await flushAttendeeSaves()

  // Always refresh attendees list to catch newly saved attendees
  await loadAttendees()

  // If no saved attendees, directly start new attendee flow
  if (!hasSavedAttendees.value) {
    emit('new')

    return
  }

  isOpen.value = true
  searchQuery.value = ''
}

// Close dialog whenever selection changes
watch(() => props.modelValue, () => {
  isOpen.value = false
})

// Pre-load attendees when component mounts (if user is logged in)
onMounted(() => {
  if (authStore.user) {
    loadAttendees()
  }
})

// Also load when user logs in
watch(() => authStore.user, newUser => {
  if (newUser && !isInitialLoadDone.value) {
    loadAttendees()
  }
})

// Expose openPicker and hasSavedAttendees for parent components
defineExpose({
  openPicker,
  hasSavedAttendees,
})

// Format date for display - imported from ~/utils/dates
</script>

<template>
  <div class="saved-attendee-picker">
    <!-- Picker Button -->
    <VBtn
      variant="tonal"
      color="primary"
      :disabled="disabled || !authStore.user"
      block
      @click="openPicker"
    >
      <VIcon
        start
        icon="bx-user-plus"
      />
      {{ modelValue || hasSelection ? 'Deelnemer wisselen' : (hasSavedAttendees ? 'Deelnemer koppelen' : 'Deelnemer toevoegen') }}
    </VBtn>

    <!-- Selected Attendee Card -->
    <VCard
      v-if="modelValue"
      variant="text"
      class="selected-attendee-card"
    >
      <VCardText>
        <div class="d-flex align-center justify-space-between">
          <div>
            <div class="font-weight-medium">
              {{ getAttendeeDisplayName(modelValue) }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ formatDate(modelValue.dateOfBirth) }} • {{ calculateAge(modelValue.dateOfBirth) }} jaar
            </div>
          </div>
          <div class="d-flex gap-2">
            <VBtn
              icon
              size="small"
              variant="text"
              :disabled="disabled"
              @click="openPicker"
            >
              <VIcon icon="bx-refresh" />
              <VTooltip
                activator="parent"
                location="top"
              >
                Andere deelnemer kiezen
              </VTooltip>
            </VBtn>
            <VBtn
              icon
              size="small"
              variant="text"
              color="error"
              :disabled="disabled"
              @click="clearSelection"
            >
              <VIcon icon="bx-x" />
              <VTooltip
                activator="parent"
                location="top"
              >
                Handmatig invullen
              </VTooltip>
            </VBtn>
          </div>
        </div>
      </VCardText>
    </VCard>

    <!-- Picker Dialog -->
    <VDialog
      v-model="isOpen"
      max-width="500"
      scrollable
    >
      <VCard>
        <VCardTitle class="d-flex align-center pb-0">
          <VIcon
            icon="bx-user-check"
            class="me-2"
          />
          Opgeslagen deelnemers
        </VCardTitle>

        <VCardText>
          <!-- Loading State -->
          <div
            v-if="isLoading"
            class="text-center py-6"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
            <p class="text-body-2 text-medium-emphasis mt-2">
              Laden...
            </p>
          </div>

          <!-- Empty State -->
          <VAlert
            v-else-if="attendees.length === 0"
            type="info"
            variant="tonal"
          >
            <template #title>
              Geen opgeslagen deelnemers
            </template>
            Je hebt nog geen deelnemers opgeslagen. Vul het formulier in en vink "Opslaan voor volgende keer" aan om deelnemers op te slaan.
          </VAlert>
          <!-- Attendee List -->
          <VList
            v-else
            class="attendee-list"
          >
            <VListItem
              v-for="attendee in filteredAttendees"
              :key="attendee.id"
              class="attendee-list-item mb-2 rounded"
              :disabled="isAttendeeDisabled(attendee)"
              @click="selectAttendee(attendee)"
            >
              <template #prepend>
                <VAvatar
                  :color="attendee.isPrimary ? 'primary' : 'secondary'"
                  size="45"
                >
                  <VIcon icon="bx-user" />
                </VAvatar>
              </template>

              <VListItemTitle class="font-weight-medium">
                {{ attendee.firstName }} {{ attendee.lastName }}
                <VChip
                  v-if="attendee.isPrimary"
                  size="x-small"
                  color="primary"
                  variant="tonal"
                  class="ms-2"
                >
                  Standaard
                </VChip>
              </VListItemTitle>

              <VListItemSubtitle>
                <span class="me-3">
                  <VIcon
                    icon="bx-calendar"
                    size="14"
                    class="me-1"
                  />
                  {{ formatDate(attendee.dateOfBirth) }} ({{ calculateAge(attendee.dateOfBirth) }} jaar)
                </span>
                <span v-if="attendee.rijksregisternummer">
                  <VIcon
                    icon="bx-id-card"
                    size="14"
                    class="me-1"
                  />
                  {{ maskRijksregisternummer(attendee.rijksregisternummer) }}
                </span>
              </VListItemSubtitle>

              <template #append>
                <VIcon
                  :icon="isAttendeeDisabled(attendee) ? 'bx-lock-alt' : 'bx-chevron-right'"
                  :color="isAttendeeDisabled(attendee) ? 'disabled' : 'primary'"
                />
              </template>
            </VListItem>
          </VList>
        </VCardText>

        <VDivider />

        <VCardActions>
          <VBtn
            variant="tonal"
            color="primary"
            @click="() => { emit('new'); isOpen = false }"
          >
            Nieuwe deelnemer
          </VBtn>
          <VSpacer />
          <VBtn
            variant="text"
            @click="isOpen = false"
          >
            Annuleren
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped lang="scss">
.saved-attendee-picker {
  .attendee-list-item {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: rgb(var(--v-theme-primary));
      background-color: rgba(var(--v-theme-primary), 0.08);
    }
  }
}
</style>
